<template>
  <main class="juliam-menu">
    <div class="scanlines" aria-hidden="true"></div>

    <header class="topbar">
      <router-link class="brand" to="/menu" aria-label="Juliam Arcade, inicio">
        <span class="brand-mark" aria-hidden="true">J</span>
        <span class="brand-copy">
          <b>JuLiam</b>
          <strong>ARCADE</strong>
        </span>
      </router-link>

      <nav class="category-nav" aria-label="Categorías del menú">
        <a href="#pases">Combos</a>
        <a
          v-for="category in navCategories"
          :key="category.id_token || category.slug"
          :href="`#category-${category.slug || category.id_token}`"
        >
          {{ category.name }}
        </a>
      </nav>

      <router-link class="wallet-link" :to="walletRoute">
        <span aria-hidden="true">◇</span>
        <span><small>Mi saldo</small>{{ walletLabel }}</span>
      </router-link>
    </header>

    <section class="hero" id="inicio">
      <div class="hero-copy">
        <p class="status"><i></i> MENÚ GAMER · JULIAM ARCADE</p>
        <h1>Comida +<br><em>tiempo de juego</em></h1>
        <p class="hero-description">
          Arma la reta, elige tu combo y disfruta consolas, arcade, snacks
          y bebidas en una sola partida.
        </p>
        <div class="hero-actions">
          <a class="primary-action" href="#pases">Ver combos <span>↓</span></a>
          <span class="open-status"><b>PLAYER 01</b> READY</span>
        </div>
      </div>

      <div class="cabinet" aria-hidden="true">
        <div class="cabinet-top">JULIAM ARCADE</div>
        <div class="cabinet-screen">
          <div class="pixel-stars">· ✦ ·<br>· ✦</div>
          <div class="controller">
            <span class="controller-cross">+</span>
            <span class="controller-core">J</span>
            <span class="controller-buttons">● ●</span>
          </div>
          <p>PRESS START</p>
        </div>
        <div class="cabinet-controls"><i></i><i></i><i></i></div>
      </div>
    </section>

    <DesktopMenuDisplay
      v-if="!loading"
      :features="spotlightItems"
      :categories="menuCategories"
      :product-price="productPrice"
      :product-detail="productDetail"
      :tone-for="toneFor"
    />

    <section v-if="spotlightItems.length" class="passes" id="pases" aria-labelledby="passes-title">
      <div class="section-heading">
        <span>01</span>
        <div>
          <small>COMIDA + JUEGO</small>
          <h2 id="passes-title">Combos Prestige</h2>
          <p>Todos incluyen papas individuales y refresco de lata de 355 ml.</p>
        </div>
      </div>

      <div class="pass-grid">
        <article
          v-for="(item, index) in spotlightItems"
          :key="item.id_token || `${item.name}-${index}`"
          class="pass-card"
          :class="{ featured: index === 3 }"
          :data-tone="toneFor(index)"
        >
          <div>
            <h3>{{ item.name }}</h3>
            <p>{{ item.detail }}</p>
          </div>
          <strong>{{ item.price }}</strong>
        </article>
      </div>
    </section>

    <div v-if="loading" class="state-card" role="status">Cargando menú...</div>

    <section v-else class="menu-board" aria-label="Menú de Juliam Arcade">
      <article
        v-for="(category, index) in menuCategories"
        :id="`category-${category.slug || category.id_token}`"
        :key="category.id_token || category.slug"
        class="menu-panel"
        :data-tone="toneFor(index)"
      >
        <header>
          <span>NIVEL {{ String(index + 1).padStart(2, '0') }}</span>
          <h2>{{ category.badge_label || category.name }}</h2>
          <p v-if="category.description">{{ category.description }}</p>
        </header>

        <div class="menu-list" :class="{ columns: Number(category.product_columns) === 2 }">
          <div
            v-for="product in category.products"
            :key="product.id_token || product.name"
            class="menu-item"
            :class="{ soldout: product.availability_status === 'sold_out' }"
          >
            <div>
              <h3>{{ product.name }}</h3>
              <p>{{ productDetail(product) }}</p>
              <span v-if="product.availability_status === 'sold_out'" class="soldout-label">Agotado</span>
            </div>
            <i aria-hidden="true"></i>
            <strong>{{ productPrice(product) }}</strong>
          </div>
        </div>
      </article>
    </section>

    <footer class="arcade-footer">
      <div class="footer-message">
        <span class="live-dot"></span>
        <p><small>JUEGA · ACUMULA · REGRESA</small><strong>Tu diversión también suma puntos</strong></p>
      </div>
      <router-link :to="walletRoute">Abrir mi wallet <span>→</span></router-link>
      <button type="button" @click="shareMenu">{{ shareLabel }}</button>
      <p class="footer-domain">juliam.gestiontotal.app</p>
    </footer>

    <WhatsAppButton />
  </main>
</template>

<script>
import { getPublicMenu } from '@/services/menuApi'
import { connectMenuSocket, closeMenuSocket } from '@/services/menuSocket'
import DesktopMenuDisplay from '@/components/menu/DesktopMenuDisplay.vue'
import WhatsAppButton from '@/components/WhatsAppButton.vue'

const fallbackCategories = [
  {
    id_token: 'juliam-snacks', slug: 'snacks', name: 'Snacks individuales', badge_label: 'Snacks individuales', product_columns: 1,
    products: [
      { id_token: 'hamburguesa-sencilla', name: 'Hamburguesa sencilla', description: 'Carne de res, queso americano, vegetales y aderezos', price: 75 },
      { id_token: 'hamburguesa-gamer', name: 'Hamburguesa Gamer Especial', description: 'Doble carne, queso, tocino y salsa BBQ', price: 110 },
      { id_token: 'boneless-individual', name: 'Boneless individual', description: '250 g · BBQ, Buffalo o Mango Habanero', price: 95 },
      { id_token: 'boneless-familiar', name: 'Alitas / Boneless familiar', description: '500 g · para compartir en tus retas', price: 170 },
      { id_token: 'papas-sencillas', name: 'Papas a la francesa sencillas', description: 'Con sal, cátsup y salsa picante', price: 40 },
      { id_token: 'papas-especiales', name: 'Papas especiales', description: 'Bañadas en queso cheddar caliente y trozos de tocino crujiente', price: 65 }
    ]
  },
  {
    id_token: 'juliam-bebidas', slug: 'bebidas', name: 'Bebidas', badge_label: 'Bebidas', product_columns: 1,
    products: [
      { id_token: 'refresco-lata', name: 'Refresco de lata', description: '355 ml · variedad de sabores', price: 25 },
      { id_token: 'agua-embotellada', name: 'Agua embotellada', description: '600 ml', price: 20 },
      { id_token: 'bebida-energetica', name: 'Bebida energética / Gatorade', description: 'Bebida individual', price: 40 }
    ]
  },
  {
    id_token: 'juliam-juegos', slug: 'juegos', name: 'Consolas y recreativos', badge_label: 'Consolas y recreativos', product_columns: 1,
    products: [
      { id_token: 'playstation-5', name: 'PlayStation 5', description: '1 hora de juego libre · incluye 1 control', price: 45 },
      { id_token: 'xbox-one', name: 'Xbox One', description: '1 hora de juego libre · controles extra +$20 MXN', price: 40 },
      { id_token: 'garra-grande', name: 'Máquina de garra grande', description: 'Por intento', price: 10 },
      { id_token: 'garra-chica', name: 'Máquina de garra chica', description: 'Por intento', price: 5 },
      { id_token: 'cabinet-arcade', name: 'Cabinets arcade', description: 'Precio por partida', price: 10 },
      { id_token: 'futbolito', name: 'Futbolito de madera', description: '5 bolas', price: 5 },
      { id_token: 'vending', name: 'Vending pedestal', description: 'Por producto', price: 10 }
    ]
  },
  {
    id_token: 'juliam-antojos', slug: 'antojos', name: 'Antojos rápidos', badge_label: 'Antojos rápidos', product_columns: 1,
    products: [
      { id_token: 'maruchan-preparada', name: 'Sopa Maruchan preparada', description: 'Limón, salsa de la casa y chilito', price: 30 },
      { id_token: 'palomitas', name: 'Palomitas de microondas', description: 'Naturales o mantequilla', price: 35 },
      { id_token: 'papas-sol', name: 'Papas en bolsa Sol', description: 'Crujientes y listas para disfrutar · preparación con salsas y limón disponible', price: 25 }
    ]
  }
]

const fallbackFeatures = [
  { id_token: 'combo-single-player', name: 'Combo Single Player', description: '1 hora de juego + hamburguesa sencilla con queso', price: 120 },
  { id_token: 'combo-speedrun', name: 'Combo Speedrun', description: '1 hora de juego + orden individual de boneless de 6–8 piezas', price: 140 },
  { id_token: 'combo-pareja', name: 'Combo Pareja', description: 'Para 2 personas · 2 horas · 1 consola, 2 controles · 1 orden grande de papas preparadas + 2 refrescos', price: 175 },
  { id_token: 'combo-squad', name: 'Combo Squad', description: 'Para 3–4 amigos · 2 horas · 1 consola, 2 controles · 1 orden de boneless + 1 orden de papas sencillas + 4 refrescos', price: 320 }
]

export default {
  name: 'PublicMenu',
  components: { DesktopMenuDisplay, WhatsAppButton },
  data() {
    return {
      loading: true,
      usingFallback: false,
      categories: [],
      features: [],
      settings: {},
      socketListener: null,
      reloadTimer: null,
      shareLabel: 'Compartir menú'
    }
  },
  computed: {
    isLoggedIn() {
      return Boolean(this.$store.state.user && this.$store.state.user.token)
    },
    walletRoute() {
      return this.isLoggedIn ? '/wallet' : '/login'
    },
    walletLabel() {
      return this.isLoggedIn ? 'Wallet' : 'Entrar'
    },
    menuCategories() {
      return (this.categories || []).filter(category => Array.isArray(category.products) && category.products.length)
    },
    navCategories() {
      return this.menuCategories.slice(0, 4)
    },
    spotlightItems() {
      const source = this.features.length
        ? this.features
        : fallbackFeatures

      return source.map(item => ({
        ...item,
        detail: item.description || item.serving_label || 'Disponible ahora',
        price: this.productPrice(item)
      }))
    }
  },
  async created() {
    await this.loadMenu()
    this.socketListener = () => this.queueReload()
    connectMenuSocket(this.socketListener)
  },
  beforeUnmount() {
    clearTimeout(this.reloadTimer)
    closeMenuSocket(this.socketListener)
  },
  methods: {
    async loadMenu() {
      try {
        const data = await getPublicMenu()
        const receivedCategories = Array.isArray(data && data.categories) ? data.categories : []
        this.categories = receivedCategories.length ? receivedCategories : fallbackCategories
        this.features = Array.isArray(data && data.features) && data.features.length ? data.features : fallbackFeatures
        this.settings = (data && data.settings) || {}
        this.usingFallback = receivedCategories.length === 0
      } catch (_error) {
        this.categories = fallbackCategories
        this.features = fallbackFeatures
        this.usingFallback = true
      } finally {
        this.loading = false
      }
    },
    queueReload() {
      clearTimeout(this.reloadTimer)
      this.reloadTimer = setTimeout(() => this.loadMenu(), 260)
    },
    toneFor(index) {
      return ['cyan', 'pink', 'lime', 'violet'][index % 4]
    },
    money(value) {
      if (value === null || value === undefined || value === '') return ''
      return new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', maximumFractionDigits: 0
      }).format(Number(value))
    },
    productPrice(product) {
      if (product.price_label) return product.price_label
      if (product.price !== null && product.price !== undefined && product.price !== '') return this.money(product.price)
      const prices = [product.price_hot, product.price_cold]
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(value => this.money(value))
      return prices.length ? prices.join(' / ') : '—'
    },
    productDetail(product) {
      if (product.description) return product.description
      if (product.serving_label) return product.serving_label
      if (product.has_hot && product.has_cold) return 'Caliente / frío'
      if (product.has_hot) return 'Caliente'
      if (product.has_cold) return 'Frío'
      return 'Disponible ahora'
    },
    async shareMenu() {
      const shareData = {
        title: 'Menú Juliam Arcade',
        text: 'Mira el menú de Juliam Arcade',
        url: 'https://juliam.gestiontotal.app/menu'
      }

      try {
        if (navigator.share) {
          await navigator.share(shareData)
        } else {
          await navigator.clipboard.writeText(shareData.url)
          this.shareLabel = 'Liga copiada'
          setTimeout(() => { this.shareLabel = 'Compartir menú' }, 1800)
        }
      } catch (_error) {
        this.shareLabel = 'No se pudo compartir'
        setTimeout(() => { this.shareLabel = 'Compartir menú' }, 1800)
      }
    }
  }
}
</script>

<style scoped>
.juliam-menu {
  --ink: #050813;
  --panel: #0b1020;
  --text: #f5f7ff;
  --muted: #949db8;
  --cyan: #4cf5ff;
  --pink: #ff3fd8;
  --lime: #a7ff4f;
  --violet: #8d72ff;
  width: 100%;
  min-height: 100vh;
  color: var(--text);
  background:
    radial-gradient(circle at 82% 14%, rgba(123, 92, 255, .22), transparent 31%),
    radial-gradient(circle at 18% 35%, rgba(255, 63, 216, .12), transparent 30%),
    linear-gradient(140deg, #040611 0%, #070b18 48%, #050814 100%);
  overflow: hidden;
  position: relative;
  isolation: isolate;
  text-align: left;
  scroll-behavior: smooth;
}

.juliam-menu::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -2;
  opacity: .16;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(76, 245, 255, .18) 1px, transparent 1px),
    linear-gradient(90deg, rgba(76, 245, 255, .18) 1px, transparent 1px);
  background-size: 54px 54px;
  mask-image: linear-gradient(to bottom, transparent, black 35%, transparent 95%);
}

.scanlines {
  position: fixed;
  inset: 0;
  z-index: 9;
  pointer-events: none;
  opacity: .14;
  background: repeating-linear-gradient(to bottom, transparent 0 4px, rgba(255, 255, 255, .035) 5px);
}

.topbar,
.hero,
.passes,
.menu-board,
.arcade-footer,
.state-card {
  width: min(1760px, calc(100% - 72px));
  margin-inline: auto;
}

.topbar {
  height: 92px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, .11);
}

.brand { display: inline-flex; align-items: center; gap: 13px; justify-self: start; text-decoration: none; }
.brand-mark {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border: 2px solid var(--cyan);
  color: var(--pink);
  font: 900 27px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  transform: rotate(-3deg);
  box-shadow: 0 0 18px rgba(76, 245, 255, .35), inset 0 0 14px rgba(255, 63, 216, .16);
  clip-path: polygon(10% 0, 100% 0, 100% 78%, 77% 100%, 0 100%, 0 12%);
}
.brand-copy { display: grid; line-height: .85; }
.brand-copy b { color: #fff; font-size: 20px; letter-spacing: -.05em; }
.brand-copy strong { color: var(--cyan); font: 900 25px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .09em; text-shadow: 0 0 16px rgba(76, 245, 255, .55); }

.category-nav { display: flex; align-items: center; gap: 24px; }
.category-nav a {
  padding: 35px 0 31px;
  color: #b8bfd2;
  font: 700 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: .1em;
  text-transform: uppercase;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: color .18s ease, border-color .18s ease;
}
.category-nav a:hover, .category-nav a:focus-visible { color: var(--cyan); border-color: var(--cyan); outline: 0; }

.wallet-link {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 15px;
  border: 1px solid rgba(76, 245, 255, .38);
  color: #fff;
  background: rgba(76, 245, 255, .055);
  font: 800 13px/1.05 ui-monospace, SFMono-Regular, Menlo, monospace;
  text-transform: uppercase;
  letter-spacing: .08em;
  text-decoration: none;
}
.wallet-link > span:first-child { color: var(--cyan); font-size: 27px; text-shadow: 0 0 12px var(--cyan); }
.wallet-link span:last-child { display: grid; gap: 3px; }
.wallet-link small { color: var(--muted); font-size: 8px; letter-spacing: .16em; }

.hero {
  min-height: min(58vw, 650px);
  display: grid;
  grid-template-columns: 1.2fr .8fr;
  align-items: center;
  gap: 8vw;
  padding: clamp(46px, 6vw, 92px) clamp(16px, 4vw, 70px) 58px;
  position: relative;
}
.hero::after {
  content: '';
  position: absolute;
  right: 2%;
  bottom: 0;
  width: 52%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--pink), transparent);
  box-shadow: 0 0 13px var(--pink);
}
.status { display: flex; align-items: center; gap: 11px; margin: 0 0 24px; color: var(--cyan); font: 700 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .2em; }
.status i { width: 8px; height: 8px; border-radius: 50%; background: var(--lime); box-shadow: 0 0 14px var(--lime); }
.hero h1 { margin: 0; color: #fff; font-size: clamp(54px, 6.7vw, 118px); line-height: .83; letter-spacing: -.07em; text-transform: uppercase; }
.hero h1 em { color: transparent; font-style: normal; -webkit-text-stroke: 1.5px var(--pink); filter: drop-shadow(0 0 9px rgba(255, 63, 216, .52)); }
.hero-description { max-width: 590px; margin: 33px 0 0; color: #b8bfd2; font-size: clamp(15px, 1.15vw, 19px); line-height: 1.65; }
.hero-actions { display: flex; align-items: center; gap: 30px; margin-top: 32px; }
.primary-action {
  display: inline-flex;
  align-items: center;
  gap: 22px;
  padding: 14px 18px;
  border: 1px solid var(--pink);
  color: white;
  background: rgba(255, 63, 216, .1);
  box-shadow: 0 0 19px rgba(255, 63, 216, .16);
  font: 800 12px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: .1em;
  text-transform: uppercase;
  text-decoration: none;
}
.primary-action span { color: var(--pink); }
.open-status { color: var(--muted); font: 600 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .08em; }
.open-status b { color: var(--lime); margin-right: 8px; }

.cabinet { width: min(100%, 470px); justify-self: center; filter: drop-shadow(0 0 38px rgba(123, 92, 255, .32)); transform: perspective(800px) rotateY(-5deg); }
.cabinet-top { width: 84%; margin: 0 auto -1px; padding: 14px; text-align: center; color: var(--pink); border: 2px solid var(--pink); border-bottom: 0; background: #0b0f22; font: 900 13px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .3em; box-shadow: 0 0 20px rgba(255, 63, 216, .35), inset 0 0 22px rgba(255, 63, 216, .08); }
.cabinet-screen { aspect-ratio: 1.15/1; display: grid; place-items: center; position: relative; border: 3px solid var(--cyan); border-radius: 18px 18px 42px 42px; background: radial-gradient(circle at 50% 40%, #15244c 0, #080d21 55%, #040610 100%); box-shadow: 0 0 24px rgba(76, 245, 255, .35), inset 0 0 42px rgba(76, 245, 255, .12); }
.pixel-stars { position: absolute; inset: 11% 12%; color: rgba(167, 255, 79, .55); font: 24px/2 ui-monospace, SFMono-Regular, Menlo, monospace; }
.controller { width: 68%; height: 38%; margin-top: 2%; display: grid; grid-template-columns: 1fr 1fr 1fr; align-items: center; justify-items: center; border: 7px solid var(--cyan); border-radius: 34% 34% 46% 46%; color: var(--cyan); box-shadow: 0 0 15px var(--cyan), inset 0 0 15px rgba(76, 245, 255, .4); transform: rotate(-2deg); }
.controller-cross { font: 900 54px/1 ui-monospace, SFMono-Regular, Menlo, monospace; }
.controller-core { width: 55px; height: 55px; display: grid; place-items: center; border: 3px solid var(--pink); border-radius: 50%; color: white; font: 900 22px/1 ui-monospace, SFMono-Regular, Menlo, monospace; box-shadow: 0 0 12px var(--pink); }
.controller-buttons { color: var(--lime); font-size: 24px; word-spacing: 8px; text-shadow: 0 0 10px var(--lime); }
.cabinet-screen p { position: absolute; bottom: 8%; margin: 0; color: var(--pink); font: 800 10px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .32em; animation: blink 1.6s steps(1) infinite; }
.cabinet-controls { width: 86%; height: 66px; margin: -9px auto 0; display: flex; justify-content: center; gap: 34px; align-items: center; border: 2px solid var(--pink); border-top-width: 6px; background: #0b0f22; clip-path: polygon(6% 0, 94% 0, 100% 100%, 0 100%); }
.cabinet-controls i { width: 17px; height: 17px; border: 2px solid var(--lime); border-radius: 50%; box-shadow: 0 0 10px var(--lime); }
.cabinet-controls i:first-child { width: 14px; height: 34px; border-radius: 3px; transform: translateY(-10px); border-color: var(--cyan); box-shadow: 0 0 10px var(--cyan); }
@keyframes blink { 50% { opacity: .2; } }

.passes { padding: 46px 0 52px; border-top: 1px solid rgba(255, 255, 255, .1); }
.section-heading { display: flex; align-items: center; gap: 18px; margin-bottom: 24px; }
.section-heading > span { color: var(--pink); font: 900 31px/1 ui-monospace, SFMono-Regular, Menlo, monospace; text-shadow: 0 0 15px rgba(255, 63, 216, .45); }
.section-heading > div { padding-left: 18px; border-left: 1px solid rgba(255, 255, 255, .18); }
.section-heading small, .menu-panel header span { color: var(--muted); font: 700 9px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .18em; text-transform: uppercase; }
.section-heading h2 { margin: 4px 0 0; color: #fff; font-size: 25px; line-height: 1; text-transform: uppercase; }
.section-heading p { margin: 9px 0 0; color: var(--muted); font-size: 12px; line-height: 1.45; }
.pass-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(245px, 1fr)); gap: 13px; align-items: stretch; }
.pass-card { min-width: 0; min-height: 176px; height: 100%; padding: 24px; display: flex; flex-direction: column; align-items: flex-start; justify-content: space-between; gap: 22px; border: 1px solid rgba(255, 255, 255, .13); background: rgba(11, 16, 32, .84); position: relative; transition: transform .18s ease, border-color .18s ease; }
.pass-card:hover { transform: translateY(-3px); border-color: var(--tone); }
[data-tone='cyan'] { --tone: var(--cyan); }
[data-tone='pink'] { --tone: var(--pink); }
[data-tone='lime'] { --tone: var(--lime); }
[data-tone='violet'] { --tone: var(--violet); }
.pass-card.featured { border-color: rgba(255, 63, 216, .6); box-shadow: inset 0 -2px 0 var(--pink), 0 0 24px rgba(255, 63, 216, .09); }
.pass-card h3 { margin: 0 0 9px; color: #fff; font-size: 16px; text-transform: uppercase; overflow-wrap: anywhere; }
.pass-card p { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.55; overflow-wrap: anywhere; }
.pass-card > strong { color: var(--tone); font: 900 clamp(23px, 2vw, 38px)/1 ui-monospace, SFMono-Regular, Menlo, monospace; text-shadow: 0 0 13px color-mix(in srgb, var(--tone) 55%, transparent); white-space: nowrap; }
.popular { position: absolute; top: -9px; right: 17px; padding: 5px 8px; background: var(--pink); color: #090612; font: 900 8px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .12em; }

.state-card, .connection-note { border: 1px solid rgba(76, 245, 255, .32); background: rgba(76, 245, 255, .055); color: #c7fbff; }
.state-card { margin-block: 40px; padding: 24px; text-align: center; }
.menu-board { padding: 26px 0 52px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; align-items: start; }
.connection-note { grid-column: 1 / -1; margin: 0 0 8px; padding: 12px 16px; font-size: 12px; }
.menu-panel { scroll-margin-top: 20px; border: 1px solid rgba(255, 255, 255, .12); background: linear-gradient(145deg, rgba(17, 24, 45, .94), rgba(7, 11, 25, .96)); }
.menu-panel header { padding: 25px 26px 20px; border-bottom: 1px solid rgba(255, 255, 255, .1); position: relative; }
.menu-panel header::before { content: ''; position: absolute; width: 48px; height: 3px; top: -2px; left: 26px; background: var(--tone); box-shadow: 0 0 12px var(--tone); }
.menu-panel header span { color: var(--tone); }
.menu-panel h2 { margin: 8px 0 0; color: #fff; font-size: clamp(19px, 1.5vw, 26px); text-transform: uppercase; }
.menu-panel header p { margin: 9px 0 0; color: var(--muted); font-size: 11px; line-height: 1.5; }
.menu-list { padding: 10px 26px 19px; }
.menu-list.columns { display: grid; grid-template-columns: 1fr 1fr; column-gap: 24px; }
.menu-item { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; padding: 14px 0; border-bottom: 1px solid rgba(255, 255, 255, .075); }
.menu-item:last-child { border-bottom: 0; }
.menu-item h3 { margin: 0 0 4px; color: #fff; font-size: 13px; }
.menu-item p { margin: 0; color: var(--muted); font-size: 10px; }
.menu-item i { align-self: end; margin-bottom: 5px; border-bottom: 1px dotted rgba(255, 255, 255, .24); }
.menu-item > strong { color: var(--tone); font: 800 16px/1 ui-monospace, SFMono-Regular, Menlo, monospace; white-space: nowrap; }
.menu-item.soldout { opacity: .48; }
.soldout-label { display: inline-block; margin-top: 6px; color: var(--pink); font: 800 8px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .12em; text-transform: uppercase; }

.arcade-footer { min-height: 110px; display: grid; grid-template-columns: minmax(260px, 1fr) auto auto 1fr; align-items: center; gap: 18px; border-top: 1px solid rgba(255, 255, 255, .13); }
.footer-message { display: flex; align-items: center; gap: 14px; }
.live-dot { width: 16px; height: 16px; border: 2px solid var(--lime); transform: rotate(45deg); box-shadow: 0 0 10px var(--lime); }
.footer-message p { margin: 0; display: grid; gap: 5px; }
.footer-message small { color: var(--lime); font: 700 8px/1 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .18em; }
.footer-message strong { color: #fff; font-size: 13px; }
.arcade-footer > a, .arcade-footer > button { padding: 13px 17px; border: 1px solid var(--cyan); color: var(--cyan); background: rgba(76, 245, 255, .035); font: 800 10px/1 ui-monospace, SFMono-Regular, Menlo, monospace; text-transform: uppercase; letter-spacing: .08em; box-shadow: 0 0 17px rgba(76, 245, 255, .09); text-decoration: none; cursor: pointer; }
.arcade-footer > a span { margin-left: 13px; }
.arcade-footer > button { border-color: rgba(255, 255, 255, .2); color: #c2c8d8; }
.footer-domain { justify-self: end; margin: 0; color: var(--muted); font: 600 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace; }

@media (min-width: 1121px) {
  .hero,
  .passes,
  .menu-board {
    display: none;
  }
}

@media (max-width: 1360px) {
  .menu-board { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 1120px) {
  .topbar { grid-template-columns: 1fr auto; }
  .category-nav { display: none; }
  .hero { grid-template-columns: 1fr .7fr; gap: 30px; padding-inline: 0; }
  .hero h1 { font-size: clamp(52px, 9vw, 82px); }
  .controller { border-width: 4px; }
  .controller-cross { font-size: 38px; }
  .controller-core { width: 42px; height: 42px; }
  .menu-board { grid-template-columns: 1fr 1fr; }
  .arcade-footer { grid-template-columns: 1fr auto auto; padding: 22px 0; }
  .footer-domain { grid-column: 1 / -1; justify-self: center; }
}

@media (max-width: 680px) {
  .juliam-menu { overflow: clip; }
  .topbar, .hero, .passes, .menu-board, .arcade-footer, .state-card { width: min(100% - 32px, 620px); }
  .topbar { height: 76px; }
  .brand-mark { width: 39px; height: 39px; font-size: 22px; }
  .brand-copy b { font-size: 16px; }
  .brand-copy strong { font-size: 19px; }
  .wallet-link { padding: 9px 10px; }
  .wallet-link > span:first-child, .wallet-link small { display: none; }
  .hero { min-height: auto; grid-template-columns: 1fr; padding: 56px 0 44px; }
  .hero-copy { text-align: center; }
  .status, .hero-actions { justify-content: center; }
  .hero h1 { font-size: clamp(48px, 16vw, 76px); line-height: .88; }
  .hero-description { margin-inline: auto; font-size: 15px; }
  .hero-actions { flex-direction: column; gap: 20px; }
  .cabinet { width: min(86%, 350px); transform: none; margin-top: 8px; }
  .passes { padding-top: 38px; }
  .pass-grid, .menu-board { grid-template-columns: 1fr; }
  .pass-card { min-height: 150px; padding: 21px; }
  .menu-list.columns { grid-template-columns: 1fr; }
  .arcade-footer { padding: 27px 0 34px; grid-template-columns: 1fr; text-align: center; }
  .footer-message { justify-content: center; }
  .arcade-footer > a, .arcade-footer > button, .footer-domain { justify-self: center; }
  .footer-domain { grid-column: auto; }
}

@media (prefers-reduced-motion: reduce) {
  .juliam-menu { scroll-behavior: auto; }
  *, *::before, *::after { animation-duration: .001ms !important; transition-duration: .001ms !important; }
}
</style>
